import { Alert, Box, Button, Chip, Sheet, Typography } from "@mui/joy";
import moment from "moment-timezone";
import { Playing } from "../atoms/playing";
import { Tag } from "../atoms/tag";
import { Engagement, Piece } from "../entities/missionMap/missionMap";
import { Memo } from "../organisms/memo";
import { useMissionMap } from "../templates/projectInvite";

const LEFT_HEADER_WIDTH = 120;

/**
 *
 * @returns {ReactElement} PieceLineItem page
 */
export function EngagementLineItem({
  engagement,
  releaseRequest = false,
  token,
  openProject,
  projectInfo,
}: {
  engagement: Engagement;
  releaseRequest: boolean;
  token: string;
  openProject?: () => void;
  projectInfo?: boolean;
}) {
  const mission = useMissionMap(token);

  // Program slots for this session, in backend/program order (pieces, standalone
  // movements, and breaks). Mirrors the invite email's "Program" section.
  const plays = engagement.Plays ?? [];
  const isBreakKind = (kind?: string) =>
    kind === "break" || kind === "unpaidBreak";
  // Show the program when there is at least one break, or at least one playing,
  // titled piece/movement.
  const hasProgram = plays.some(
    (p) => isBreakKind(p.Kind) || (p.Playing && (!!p.Title || !!p.Composer)),
  );

  // Parse the ISO UTC string
  const utcDate = moment.utc(engagement.DateFromUTC);

  // Convert to the local timezone
  const localDate = utcDate.tz(engagement.VenueTzName);

  return (
    <Box sx={{ display: "flex", gap: 0.5, flexDirection: "column" }}>
      {projectInfo ? (
        <Sheet
          variant="outlined"
          sx={{ display: "flex", p: 1, mb: 1, borderRadius: 8 }}
        >
          <Typography
            startDecorator={
              mission?.ProjectLogo ? (
                <img src={mission?.ProjectLogo} style={{ height: 30 }} />
              ) : undefined
            }
            level="title-md"
          >
            <i>{mission?.ProjectName}</i>
          </Typography>
        </Sheet>
      ) : (
        []
      )}
      <Box
        sx={{
          display: "flex",
          justifyContent: "start",
          alignItems: "start",
          gap: 1,
          width: "100%",
          textAlign: "left",
        }}
      >
        <Tag sx={{ width: LEFT_HEADER_WIDTH, flexShrink: 0 }}>
          <Box sx={{ textAlign: "center" }}>
            {engagement.WeekDay.toUpperCase()}
            <br />
            <Typography level="title-lg">
              {engagement.Month} {engagement.Day}
            </Typography>
            <br />
            {engagement.HourRange ? (
              <>
                <Typography level="body-xs" sx={{ fontSize: 11 }}>
                  {engagement.HourRange}
                </Typography>
                <br />
              </>
            ) : (
              []
            )}
            <Typography level="body-xs" sx={{ fontSize: 11 }}>
              {engagement.Timezone}
            </Typography>
          </Box>
        </Tag>
        <Box sx={{ flexGrow: 1 }}>
          <Typography level="body-sm" sx={{ color: "black" }}>
            {engagement.Title}
          </Typography>
          <Typography level="body-xs">{engagement.VenueName}</Typography>
          <Typography level="body-xs">{engagement.VenueAddress}</Typography>
        </Box>
        <Box sx={{ textAlign: "right" }}>
          {mission?.ShowFullSchedule ? (
            <Playing playing={engagement.Playing} />
          ) : (
            []
          )}
          {releaseRequest ? (
            <Chip size="sm" variant="solid" color="primary">
              Release Pending
            </Chip>
          ) : (
            []
          )}
        </Box>
      </Box>
      {hasProgram ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "start",
            gap: 1,
            textAlign: "left",
          }}
        >
          <Box sx={{ width: LEFT_HEADER_WIDTH, flexShrink: 0 }}>
            <Typography level="body-xs">Program:</Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            {plays.map((e, i) => {
              // Breaks (paid or unpaid) render as a centered separator with their
              // label and optional duration.
              if (isBreakKind(e.Kind)) {
                const label =
                  e.Title && e.Title !== "Break"
                    ? e.Title
                    : e.Kind === "unpaidBreak"
                      ? "Break (unpaid)"
                      : "Break";
                return (
                  <Typography
                    level="body-xs"
                    key={`break-${i}`}
                    sx={{ color: "#9e9e9e", textAlign: "center", my: 0.25 }}
                  >
                    &mdash; {label}
                    {e.Duration ? ` · ${e.Duration} min` : ""} &mdash;
                  </Typography>
                );
              }
              // Playing pieces and standalone movements.
              if (e.Playing && (e.Title || e.Composer)) {
                return (
                  <Typography
                    level="body-xs"
                    key={`play-${i}`}
                    sx={{ color: "black" }}
                  >
                    • <i>{e.Title}</i>
                    {e.Composer ? `, ${e.Composer}` : ""}
                    {e.MovementsLabel ? (
                      <span style={{ color: "#757575" }}>
                        {" "}
                        ({e.MovementsLabel})
                      </span>
                    ) : e.Kind === "movement" ? (
                      <span style={{ color: "#757575" }}> (movement)</span>
                    ) : null}
                    {e.Duration ? ` — ${e.Duration} min` : ""}
                  </Typography>
                );
              }
              return null;
            })}
          </Box>
        </Box>
      ) : (
        []
      )}
      {engagement.Memo ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "start",
            justifyContent: "start",
            textAlign: "left",
            gap: 1,
          }}
        >
          <Box sx={{ width: LEFT_HEADER_WIDTH, flexShrink: 0 }}>
            <Typography level="body-xs">Memo:</Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Memo token={token} compact memo={engagement.Memo} />
          </Box>
        </Box>
      ) : (
        []
      )}
      {engagement.Chairs?.length ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "start",
            gap: 1,
            textAlign: "left",
          }}
        >
          <Box sx={{ width: LEFT_HEADER_WIDTH, flexShrink: 0 }}></Box>
          <Box
            sx={{ display: "flex", gap: 1, flex: 1, flexDirection: "column" }}
          >
            {engagement?.Chairs?.map((e, i) => {
              const sectionInfo = [];
              if (e.SectionName) sectionInfo.push(`Section: ${e.SectionName}`);
              if (e.SectionRole) sectionInfo.push(`Role: ${e.SectionRole}`);
              // if (e.SectionOrder) sectionInfo.push(`Chair# ${e.SectionOrder}`);
              return (
                <Sheet
                  variant="plain"
                  key={e.ID ?? `${e.PieceNumber}-${i}`}
                  sx={{
                    flex: 1,
                    flexDirection: "column",
                    display: "flex",
                    alignItems: "stretch",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      flexDirection: "column",
                      alignItems: "start",
                    }}
                  >
                    {/* PieceNumber is the chair's piece position, and only a v2 project seats a
                        musician per piece. A v3 chair is work-session anchored — it has no piece —
                        so the backend leaves the position at 0 and this read "Chair for Piece #0".
                        Show the label only when it identifies an actual piece. */}
                    {e.PieceNumber > 0 ? (
                      <Typography level="body-xs" sx={{ color: "inherit" }}>
                        Chair for Piece #{e.PieceNumber}
                      </Typography>
                    ) : (
                      []
                    )}
                    {sectionInfo.length ? (
                      <Tag
                        startDecorator={<i className="fa-solid fa-chair"></i>}
                      >
                        {sectionInfo.join(", ")}
                      </Tag>
                    ) : (
                      []
                    )}
                  </Box>
                  {e.Memo ? (
                    <Memo token={token} warning compact memo={e.Memo} />
                  ) : (
                    []
                  )}
                  <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                    <Typography level="body-xs" sx={{ color: "inherit" }}>
                      Instrument{e.Instruments?.length > 1 ? "s" : ""}:
                    </Typography>
                    {e?.Instruments?.map((i) => (
                      <Tag key={i}>{i}</Tag>
                    ))}
                  </Box>
                </Sheet>
              );
            })}
          </Box>
        </Box>
      ) : (
        []
      )}
      {openProject ? (
        <Button
          endDecorator={<i className="fa-solid fa-arrow-right"></i>}
          onClick={() => openProject()}
          sx={{ mt: 1 }}
          size="lg"
        >
          View Project Details
        </Button>
      ) : (
        []
      )}
    </Box>
  );
}
